import React, { Fragment } from 'react';

// Components
import RiskProfileRuleTarget from '../../risk-profiles/RiskProfileRulesTarget';
import { IconButton, TertiaryButton } from '../../buttons';
import { PillLabel } from '../../misc';

// Libraries
import classNames from 'classnames';
import { get } from 'lodash';

// Types
import Locale from '@/types/locale';
import RiskProfileItem from '@/types/risk-profile-item';
import { Profile as PartialOmniRiskProfileConfig } from '@/types/risk-profiles/omni/full-omni-risk-profile';

// Utils
import { FormattedMessage, formatMessage, formatLocalizedString } from '../../../../utils/i18n';
import { getRiskProfileRuleTargets } from '../../../../utils/risk-profiles';

type Props = {
  riskProfile: PartialOmniRiskProfileConfig | RiskProfileItem;
  className?: string;
  locale: Locale;
  loading?: boolean;
  hideScoreRange?: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
  onAttach?: () => void;
  onSelect?: () => void;
  selectedRiskProfile?: RiskProfileItem;
};

const RiskProfileCard = (props: Props) => {
  const { riskProfile, className, locale, loading, hideScoreRange, onDelete, onEdit, onAttach, onSelect, ...rest } = props;
  const { scoreRange, rules, description } = riskProfile;

  const riskProfileConfigId = get(riskProfile, 'id') ?? get(riskProfile, '_id');

  const onDeleteClick = () => onDelete && riskProfile && onDelete();
  const onEditClick = () => onEdit && riskProfile && onEdit();
  const onAttachClick = () => onAttach && riskProfile && onAttach();
  const onSelectClick = () => onSelect && riskProfile && onSelect();

  const classes = classNames('risk-profile-card', className, {
    selectable: !!onSelect,
  });

  const isLoading = () => {
    const { loading, selectedRiskProfile } = props;
    return loading && riskProfile === selectedRiskProfile;
  };

  return (
    <div className={classes} onClick={onSelectClick} id={`risk-profile-card-${riskProfileConfigId}`}>
      <div className="profile-description">
        <div className="profile-header">
          <div className="profile-title-wrapper">
            <span className="profile-title">{formatLocalizedString(riskProfile.title, locale)}</span>

            {scoreRange && !hideScoreRange && (
              <PillLabel text={formatMessage('RiskProfileCard.ScoreRange', { min: String(scoreRange.min), max: String(scoreRange.max) })} />
            )}
          </div>

          <div className="profile-actions">
            {onDelete && <IconButton className="profile-delete" icon="ds-delete" onClick={onDeleteClick} disabled={loading} />}

            {onEdit && <IconButton className="profile-edit" icon="pencil" onClick={onEditClick} disabled={loading} />}
          </div>
        </div>

        <div className="description-text">
          <span>{formatLocalizedString(description, locale)}</span>
        </div>
      </div>

      <div className="d-flex align-items-end">
        <div className={`profile-rule-values flex-fill ${riskProfile.rules.length > 2 ? 'summaryView' : ''}`}>
          {rules.map((rule, index) => {
            const targets = getRiskProfileRuleTargets(rule);
            return (
              <Fragment key={index}>
                {targets.map(target => (
                  <RiskProfileRuleTarget key={target.key} label={target.label} value={target.value} />
                ))}
              </Fragment>
            );
          })}
        </div>

        {onAttach && (
          <TertiaryButton disabled={loading} loading={isLoading()} compact onClick={onAttachClick}>
            <FormattedMessage id="RiskProfileCard.AttachToComparison" />
          </TertiaryButton>
        )}
      </div>
    </div>
  );
};

export default RiskProfileCard;
